/**
 * JobAccessDialog — interactive per-job ACL manager (Google-Docs-style sharing).
 *
 * Imperative trigger: call showJobAccessDialog({ jobUid, jobName }) from anywhere
 * (e.g. a grid rowAction). The Subject emits to the always-mounted JobAccessDialogHost,
 * which owns the MUI Dialog lifecycle. This mirrors exactly how showForm / FormBuilderManager
 * works in form-builder-manager.tsx.
 *
 * Host is mounted in page.tsx alongside FormBuilderManager.
 */

import React, { useEffect, useMemo, useState } from "react";
import { showJobAccessDialog } from "./job-access-signal";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { errorMessage } from "../../utils/error-message";
import {
  JOB_SHARE_LEVELS,
  JobShareLevel,
  JobAccessEntry,
  listJobAccess,
  shareJob,
  unshareJob,
} from "../../services/job-share-rpc-client";
import {
  UserGroupRow,
  listUserGroups,
} from "../../services/admin-rpc-client";

// ---------------------------------------------------------------------------
// Imperative trigger — mirror of `showForm` in form-builder-manager.tsx

export interface JobAccessDialogRequest {
  jobUid: string;
  jobName: string;
}

// ---------------------------------------------------------------------------
// Host component — subscribe to the Subject and render the dialog.
// Mount this once near the app root, beside FormBuilderManager.

export const JobAccessDialogHost: React.FC = () => {
  const [req, setReq] = useState<JobAccessDialogRequest | undefined>(
    undefined,
  );

  useEffect(() => {
    const sub = showJobAccessDialog.subscribe((next) => {
      setReq(next);
    });
    return () => sub.unsubscribe();
  }, []);

  return (
    <JobAccessDialog
      open={!!req}
      jobUid={req?.jobUid ?? ""}
      jobName={req?.jobName ?? ""}
      onClose={() => showJobAccessDialog.next(undefined)}
    />
  );
};

// ---------------------------------------------------------------------------
// Core dialog

interface JobAccessDialogProps {
  open: boolean;
  jobUid: string;
  jobName: string;
  onClose: () => void;
}

const JobAccessDialog: React.FC<JobAccessDialogProps> = ({
  open,
  jobUid,
  jobName,
  onClose,
}) => {
  // Fetched data
  const [grants, setGrants] = useState<JobAccessEntry[]>([]);
  const [userGroups, setUserGroups] = useState<UserGroupRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Add-grant row state
  const [addTarget, setAddTarget] = useState<UserGroupRow | null>(null);
  const [addLevel, setAddLevel] = useState<JobShareLevel>("viewer");
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // Per-row mutation busy/error (keyed by usergroupUid)
  const [rowBusy, setRowBusy] = useState<Record<string, boolean>>({});
  const [rowError, setRowError] = useState<Record<string, string>>({});

  // Load both lists whenever the dialog opens (or the jobUid changes).
  const refresh = async () => {
    if (!jobUid) return;
    setLoading(true);
    setFetchError(null);
    try {
      const [g, ug] = await Promise.all([
        listJobAccess(jobUid),
        listUserGroups(),
      ]);
      setGrants(g);
      setUserGroups(ug);
    } catch (e) {
      setFetchError(errorMessage(e) || String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && jobUid) {
      // Reset add row when dialog opens on a new job
      setAddTarget(null);
      setAddLevel("viewer");
      setAddError(null);
      setRowBusy({});
      setRowError({});
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, jobUid]);

  // Usergroups that don't already have an active grant (exclude existing grantees
  // from the add-grant picker so an admin doesn't accidentally duplicate).
  const grantedUids = useMemo(
    () => new Set(grants.map((g) => g.usergroupUid)),
    [grants],
  );
  const availableGroups = useMemo(
    () => userGroups.filter((ug) => !grantedUids.has(ug.uid)),
    [userGroups, grantedUids],
  );

  // Change the level on an existing grant (editable Select in the table row).
  const handleLevelChange = async (
    usergroupUid: string,
    newLevel: JobShareLevel,
  ) => {
    setRowBusy((prev) => ({ ...prev, [usergroupUid]: true }));
    setRowError((prev) => ({ ...prev, [usergroupUid]: "" }));
    try {
      await shareJob(jobUid, usergroupUid, newLevel);
      await refresh();
    } catch (e) {
      setRowError((prev) => ({
        ...prev,
        [usergroupUid]: errorMessage(e) || String(e),
      }));
    } finally {
      setRowBusy((prev) => ({ ...prev, [usergroupUid]: false }));
    }
  };

  // Revoke a grant (with confirm).
  const handleRevoke = async (entry: JobAccessEntry) => {
    if (
      !window.confirm(
        `Revoke access for "${entry.name}"? They will lose all access to this job.`,
      )
    ) {
      return;
    }
    const uid = entry.usergroupUid;
    setRowBusy((prev) => ({ ...prev, [uid]: true }));
    setRowError((prev) => ({ ...prev, [uid]: "" }));
    try {
      await unshareJob(jobUid, uid);
      await refresh();
    } catch (e) {
      setRowError((prev) => ({ ...prev, [uid]: errorMessage(e) || String(e) }));
    } finally {
      setRowBusy((prev) => ({ ...prev, [uid]: false }));
    }
  };

  // Add a new grant.
  const handleAdd = async () => {
    if (!addTarget) return;
    setAddBusy(true);
    setAddError(null);
    try {
      await shareJob(jobUid, addTarget.uid, addLevel);
      setAddTarget(null);
      setAddLevel("viewer");
      await refresh();
    } catch (e) {
      setAddError(errorMessage(e) || String(e));
    } finally {
      setAddBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Job access: {jobName}</DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {fetchError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {fetchError}
          </Alert>
        )}
        {!loading && (
          <>
            {/* Current grants table */}
            {grants.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2 }}
              >
                No grants yet — admins always have full access.
              </Typography>
            ) : (
              <Table size="small" sx={{ mb: 2 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell sx={{ width: 140 }}>Level</TableCell>
                    <TableCell sx={{ width: 48 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {grants.map((entry) => {
                    const uid = entry.usergroupUid;
                    const busy = !!rowBusy[uid];
                    const err = rowError[uid];
                    return (
                      <React.Fragment key={uid}>
                        <TableRow>
                          <TableCell>{entry.name}</TableCell>
                          <TableCell>
                            <FormControl size="small" fullWidth disabled={busy}>
                              <Select
                                value={entry.perms as JobShareLevel}
                                onChange={(e) =>
                                  handleLevelChange(
                                    uid,
                                    e.target.value as JobShareLevel,
                                  )
                                }
                              >
                                {JOB_SHARE_LEVELS.map((l) => (
                                  <MenuItem key={l} value={l}>
                                    {l}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Revoke access">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={busy}
                                  onClick={() => handleRevoke(entry)}
                                  aria-label={`Revoke access for ${entry.name}`}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                        {err && (
                          <TableRow>
                            <TableCell colSpan={3} sx={{ py: 0, border: 0 }}>
                              <Alert severity="error" sx={{ py: 0 }}>
                                {err}
                              </Alert>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            )}

            {/* Add-grant row */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Add access
            </Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
              <Autocomplete
                size="small"
                sx={{ flex: 1 }}
                options={availableGroups}
                getOptionLabel={(ug) =>
                  ug.email ? `${ug.name} <${ug.email}>` : ug.name
                }
                isOptionEqualToValue={(a, b) => a.uid === b.uid}
                value={addTarget}
                onChange={(_e, val) => setAddTarget(val)}
                renderInput={(params) => (
                  <TextField {...params} label="User or group" />
                )}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel id="add-level-label">Level</InputLabel>
                <Select
                  labelId="add-level-label"
                  label="Level"
                  value={addLevel}
                  onChange={(e) => setAddLevel(e.target.value as JobShareLevel)}
                >
                  {JOB_SHARE_LEVELS.map((l) => (
                    <MenuItem key={l} value={l}>
                      {l}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                disabled={!addTarget || addBusy}
                onClick={handleAdd}
                sx={{ height: 40 }}
              >
                {addBusy ? <CircularProgress size={18} /> : "Add"}
              </Button>
            </Box>
            {addError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {addError}
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default JobAccessDialog;
